import { GetVhPixels, roundDecimal } from '@common/Common';
import GoalNavigation from "@common/Components/GoalNavigation.vue";
import PortfolioOrDecisionNavigation from "@common/Components/PortfolioOrDecisionNavigation.vue";
import { ICommandStatus } from "@cqrs/cqrs";
import { CriterionSelectorDialogViewModel } from "@Criteria/Hierarchies/Components/CriterionSelectorDialog";
import CriterionSelectorDialog from "@Criteria/Hierarchies/Components/CriterionSelectorDialog.vue";
import ScoringVotesSourceSelectorDialog from "@Evaluation/AlternativesSurveys/Components/ScoringVotesSourceSelectorDialog.vue";
import { IEvaluationCommand } from "@Evaluation/cqrs";
import { BarChartDialogViewModel } from "@Evaluation/ValueAnalysis/Components/BarChartDialog";
import BarChartDialog from "@Evaluation/ValueAnalysis/Components/BarChartDialog.vue";
import { RadarChartDialogViewModel } from "@Evaluation/ValueAnalysis/Components/RadarChartDialog";
import RadarChartDialog from "@Evaluation/ValueAnalysis/Components/RadarChartDialog.vue";
import { SaveToAttributeDialogViewModel } from "@Evaluation/ValueAnalysis/Components/SaveToAttributeDialog";
import SaveToAttributeDialog from "@Evaluation/ValueAnalysis/Components/SaveToAttributeDialog.vue";
import { SensitivityAnalysisChartDialogViewModel } from "@Evaluation/ValueAnalysis/Components/SensitivityAnalysisChartDialog";
import SensitivityAnalysisChartDialog from "@Evaluation/ValueAnalysis/Components/SensitivityAnalysisChartDialog.vue";
import { SaveResultsToExistingAttributeCommandV1, SaveResultsToNewAttributeCommandV1 } from "@Evaluation/ValueAnalysis/Domain/commands-core";
import ValueAnalysisCommands from "@Evaluation/ValueAnalysis/Domain/commands-vue-component";
import ValueAnalysisQueries from '@Evaluation/ValueAnalysis/Domain/queries-vue-component';
import { AlternativeReadModel, AttributeReadModel, CriterionReadModel, CriterionWeightReadModel, ResultsPageReadModel, ScoringPanelReadModel, ScoringPanelistReadModel, ScoringPanelistsReadModel, ViewDefReadModel, WeightsSetReadModel } from '@Evaluation/ValueAnalysis/Domain/readmodels';
import { KendoGrid, KendoGridField, KendoGridOptions, KendoGridRow } from '@Kendo/Components/KendoGrid';
import KendoGridColumnsEditorDialog from "@Kendo/Components/KendoGridColumnsEditorDialog.vue";
import Cookies from "js-cookie";
import Vue from "vue";
import { Component, mixins } from 'vue-facing-decorator';


@Component({
    components: { PortfolioOrDecisionNavigation, GoalNavigation, RadarChartDialog, BarChartDialog, SensitivityAnalysisChartDialog, SaveToAttributeDialog, ScoringVotesSourceSelectorDialog, CriterionSelectorDialog, KendoGridColumnsEditorDialog }
})
export default class ResultsPage extends mixins(ValueAnalysisQueries, ValueAnalysisCommands) {

    get workspaceId() { return Number.parseInt(this.$route.params.workspaceId); }
    get projectId() { return Number.parseInt(this.$route.params.projectId); }
    get evalModelId() { return Number.parseInt(this.$route.params.evalModelId); }

    rm: ResultsPageReadModel = null;
    get goalName(): string { return this.rm?.GoalName ?? null; }
    get precisionLabel(): string { return `${1 / Math.pow(10, this.precision)}`; }

    get criteria(): CriterionReadModel[] { return this.rm?.Criteria ?? [] }
    get parents(): CriterionReadModel[] { return this.criteria.filter(c => c.IsParent === true); }
    get selectedCriterion(): CriterionReadModel { return this.criteria.find(a => a.Id === this.rm.CriterionId) ?? null; }

    get weightsSets(): WeightsSetReadModel[] { return this.rm?.WeightsSets ?? []; }
    get selectedWeightsSet(): WeightsSetReadModel { return this.weightsSets.find(a => a.Id === this.rm.WeightsSetId) ?? null; }
    get weights(): CriterionWeightReadModel[] { return this.rm?.Weights ?? [] }

    get surveys(): ScoringPanelReadModel[] { return this.rm?.ScoringPanels ?? []; }
    get selectedSurvey(): ScoringPanelReadModel { return this.surveys.find(a => a.Id === this.rm.ScoringSurveyId) ?? null; }
    get selectedProjectUser(): ScoringPanelistReadModel { return this.projectUsers.find(a => a.Id === this.rm.ScoringProjectUserId) ?? null; }
    get selectedScoresSourceLabel(): string 
    {
        if (this.selectedSurvey)
        {
            let label = this.selectedSurvey.Name;

            if (this.selectedProjectUser)
            {
                label += " (by: " + this.selectedProjectUser.Name + ")";
            }
            else
            {
                label += " (AGG)";
            }

            return label;
        }
        else
        {
            return "None";
        }
    }

    get attributes(): AttributeReadModel[] { return this.rm?.Attributes ?? []; }

    get lists(): ViewDefReadModel[] { return this.rm?.ViewDefs ?? []; }
    get selectedList(): ViewDefReadModel { return this.lists.find(a => a.Id === this.rm.ViewDefId) ?? null; }

    get alternatives(): AlternativeReadModel[] { return this.rm?.Alternatives ?? []; }

    selectedAlternatives: AlternativeReadModel[] = [];
    projectUsers: ScoringPanelistReadModel[] = [];

    Grid: KendoGrid = null;
    chartThemes: string[] = ["default", "black", "blueopal", "bootstrap", "highcontrast", "metro", "metroblack", "moonlight", "silver", "uniform"];
    chartTheme: string = "default";
    dataReady: boolean = false;
    precision: number = 3;

    barChartDialog: BarChartDialogViewModel = new BarChartDialogViewModel();
    radarChartDialog: RadarChartDialogViewModel = new RadarChartDialogViewModel();
    sensitivityAnalysisChartDialog: SensitivityAnalysisChartDialogViewModel = new SensitivityAnalysisChartDialogViewModel();
    saveToAttributeDialog: SaveToAttributeDialogViewModel = new SaveToAttributeDialogViewModel();
    scoringVotesSourceSelectorDialog: ScoringVotesSourceSelectorDialogViewModel = new ScoringVotesSourceSelectorDialogViewModel();
    criterionSelectorDialog = new CriterionSelectorDialogViewModel();
    kendoGridColumnsEditorDialogOpened: boolean = false;

    changePrecision(val: number)
    {
        if (this.precision !== val)
        {
            this.precision = val;
            this.PersistViewSettings();
            this.SetGridData();
        }
    }

    changeWeightsSet(wsId: number)
    {
        if (this.rm.WeightsSetId !== wsId)
        {
            this.rm.WeightsSetId = wsId;
            this.reloadResults();
        }
    }

    changeScoringVotesSource(surveyId: number, projectUserId: number | null)
    {
        if (this.rm.ScoringSurveyId !== surveyId)
        {
            this.rm.ScoringSurveyId = surveyId;
            this.PersistViewSettings();
        }

        if (this.rm.ScoringProjectUserId !== projectUserId)
        {
            this.rm.ScoringProjectUserId = projectUserId;
        }

        this.reloadResults();
        this.scoringVotesSourceSelectorDialog.opened = false;
    }

    changeViewDef(viewDefId: string | null) 
    {
        if (this.rm.ViewDefId !== viewDefId)
        {
            this.rm.ViewDefId = viewDefId;
            this.PersistViewSettings();
            this.reloadResults();
        }
    }

    changeCriterion(criterionId: number)
    {
        if (this.rm.CriterionId !== criterionId)
        {
            this.rm.CriterionId = criterionId;

            const fieldsIds = this.Grid.GetShownFieldsIds().filter(f => !f.startsWith("CriteriaFields"));

            const rootField = GridHelper.GetCriterionFieldName(this.selectedCriterion.Id);
            fieldsIds.push(rootField);

            this.Grid.SetColumns(fieldsIds);
            this.Grid.Sort(rootField, "desc");
        }
    }


    showSensitivityAnalysisChartDialog()
    {
        this.sensitivityAnalysisChartDialog.show(this.selectedAlternatives, this.criteria, this.weights);

        Vue.nextTick(() =>
        {
            this.sensitivityAnalysisChartDialog.renderChart();
        });
    }

    showRadarChartDialog()
    {
        this.radarChartDialog.show(this.criteria, this.selectedAlternatives, this.chartTheme, this.precision);

        Vue.nextTick(() =>
        {
            this.radarChartDialog.renderChart();
        });
    }

    showBarChartDialog()
    {
        this.barChartDialog.show(this.criteria, this.selectedAlternatives, this.chartTheme, this.precision);

        Vue.nextTick(() =>
        {
            this.barChartDialog.renderChart();
        });
    }

    showCriterionSelectorDialog()
    {
        this.criterionSelectorDialog.show(this.selectedCriterion?.Id, this.criteria);
    }

    showScoreToAttributeSaverDialog()
    {
        this.saveToAttributeDialog.showScoreSaver(this.selectedCriterion, this.alternatives, this.precision, this.attributes);
    }

    showRankToAttributeSaverDialog()
    {
        this.saveToAttributeDialog.showRankSaver(this.selectedCriterion, this.alternatives, this.precision, this.attributes);
    }

    showScoringVotesSourceSelectorDialog()
    {
        this.scoringVotesSourceSelectorDialog.show(this);
    }


    reloadResults()
    {
        this.dataReady = false;
        this.runResultsPageQueryV1(this.workspaceId, this.projectId, this.evalModelId, this.selectedList?.Id, this.selectedWeightsSet?.Id, this.selectedSurvey?.Id, this.selectedProjectUser?.Id);
    }

    loadScoringPanelists(surveyId: number)
    {
        this.projectUsers = [];
        this.runScoringPanelistsBySurveyIdQueryV1(this.workspaceId, this.projectId, this.evalModelId, surveyId);
    }


    handleSaveResultsToNewAttributeCommand(attrName: string, values: string[])
    {
        this.saveToAttributeDialog.saving = true;
        this.invokeSaveResultsToNewAttributeCommandV1(this.rm.UserId, this.workspaceId, this.projectId, this.evalModelId, attrName, values);
    }

    handleSaveResultsToExistingAttributeCommand(attrId: number, values: string[])
    {
        this.saveToAttributeDialog.saving = true;
        this.invokeSaveResultsToExistingAttributeCommandV1(this.rm.UserId, this.workspaceId, this.projectId, this.evalModelId, attrId, values);
    }

    mounted()
    {
        const viewSettingsRaw = Cookies.get(`ViewSettings_Ranking_${this.evalModelId}`) as any;
        const viewSettings: ViewSettings = viewSettingsRaw != null ? JSON.parse(viewSettingsRaw) : null
        this.precision = viewSettings?.Precision ?? 3;

        document.title = "Ranking | TransparentChoice";
        this.runResultsPageQueryV1(this.workspaceId, this.projectId, this.evalModelId, viewSettings?.ViewDefId ?? null, null, viewSettings?.ScoringSurveyId ?? null, null);
    }

    onSetModel(model: any, modelName: string) 
    {
        if (modelName == "ResultsPageReadModel")
        {
            const rm = model as ResultsPageReadModel;

            if (this.rm == null)
            {
                this.rm = rm;
                this.dataReady = true;

                Vue.nextTick(() =>
                {
                    this.RenderGrid();
                    this.SetGridData();
                });
            }
            else
            {
                this.rm.Alternatives = rm.Alternatives;
                this.rm.Weights = rm.Weights;
                this.SetGridData();
                this.dataReady = true;
            }
        }
        else if (modelName == "ScoringPanelistsReadModel")
        {
            const rm = model as ScoringPanelistsReadModel;
            this.projectUsers = rm.Panelists;
        }
    }

    onCommandSucceed(cmd: IEvaluationCommand, correlationId: string)
    {
        if (SaveResultsToExistingAttributeCommandV1.matchType(cmd))
        {
            window.location.reload();
        }
        else if (SaveResultsToNewAttributeCommandV1.matchType(cmd))
        {
            window.location.reload();
        }
    }

    onCommandFailed(status: ICommandStatus)
    {
        if (SaveResultsToExistingAttributeCommandV1.matchType(status.Command))
        {
            this.saveToAttributeDialog.saving = false;

            for (const error of status.ValidationErrors)
            {
                alert(error.Error);
            }
        }
        else if (SaveResultsToNewAttributeCommandV1.matchType(status.Command))
        {
            this.saveToAttributeDialog.saving = false;

            for (const error of status.ValidationErrors)
            {
                alert(error.Error);
            }
        }
    }


    PersistViewSettings()
    {
        const viewSettings = new ViewSettings();
        viewSettings.ViewDefId = this.selectedList?.Id ?? null;
        viewSettings.ScoringSurveyId = this.selectedSurvey?.Id ?? null,
            viewSettings.Precision = this.precision;

        const expiration = new Date();
        expiration.setDate(expiration.getDate() + 999);

        Cookies.set(`ViewSettings_Ranking_${this.evalModelId}`, JSON.stringify(viewSettings), { expires: expiration, path: "/" });
    }

    RenderGrid()
    {
        const fields: KendoGridField[] = [
            new KendoGridField(GridHelper.GetAlternativeFieldName("Id"), "string", false, null),
            new KendoGridField(GridHelper.GetAlternativeFieldName("Name"), "string", true, "Name", null, ""),
            new KendoGridField(GridHelper.GetAlternativeFieldName("Description"), "string", true, "Description", null, ""),
            new KendoGridField(GridHelper.GetAlternativeFieldName("Created"), "date", true, "Created", "{0:g}", ""),
            new KendoGridField(GridHelper.GetAlternativeFieldName("Modified"), "date", true, "Modified", "{0:g}", ""),
            new KendoGridField(GridHelper.GetAlternativeFieldName("StatusName"), "string", true, "Status", "{0:g}", "")
        ];

        this.attributes.forEach(attr =>
        {
            fields.push(new KendoGridField(GridHelper.GetAttributeFieldName(attr.Id), attr.GridFieldType, true, attr.Name, attr.Format, attr.Section));
        });

        this.criteria.forEach(criterion =>
        {
            const f = new KendoGridField(GridHelper.GetCriterionFieldName(criterion.Id), "number", true, criterion.Name, "{0:###.#####}", "Criteria");
            f.IsBarDefault = true;
            fields.push(f);
        });

        const buildDefaultColumns = () =>
        {
            const defaultFields: KendoGridField[] = [
                fields.find(f => f.FieldId === GridHelper.GetAlternativeFieldName("Name")),
                fields.find(f => f.FieldId === GridHelper.GetCriterionFieldName(this.selectedCriterion.Id)),
            ];

            return KendoGridOptions.BuildColumns(defaultFields);
        }

        const getSelectedIds = (e: any) =>
        {
            const selectedRows = e.sender.select();
            const selectedIds: string[] = [];

            const idFieldName = GridHelper.GetAlternativeFieldName("Id");

            for (let i = 0; i < selectedRows.length; i++)
            {
                const dataItem = e.sender.dataItem(selectedRows[i]);
                selectedIds.push(dataItem[idFieldName]);
            }

            this.selectedAlternatives = this.alternatives.filter(a => selectedIds.includes(a.Id));
        }

        const gridOptions = KendoGridOptions.CreateDefault();
        gridOptions.SetColumns(buildDefaultColumns());
        gridOptions.SetGroupable(false);
        gridOptions.SetHeight(GetVhPixels(90));
        gridOptions.SetSelectable(true);
        gridOptions.SetOnGridChange(getSelectedIds);

        const grid = new KendoGrid(GridHelper.GetAlternativeFieldName("Id"), fields, () => { this.selectedAlternatives = []; });

        grid.SetCustomColumnMenu(gridOptions);
        grid.Sort(GridHelper.GetCriterionFieldName(this.selectedCriterion.Id), "desc");

        const getColor = () => { return "bg-primary" };
        const getMaxValue = (field: string) =>
        {
            let maxValue = 100;

            //if (!field.startsWith(Func.GetCriteriaFieldsNamePrefix()))
            {
                const data = this.Grid.GetData().map(row => row[field]).filter(v => v != null);
                maxValue = Math.max(...data);
            }

            return maxValue;
        }

        grid.SetProgressBarMaxValue(getMaxValue);
        grid.SetProgressBarColor(getColor);

        this.Grid = grid;
        this.Grid.Render1("mygrid", gridOptions);
        this.Grid.RenderProgressBars();
    }

    SetGridData()
    {
        if (this.Grid)
        {
            const data: any[] = [];

            this.alternatives.forEach(alternative =>
            {
                const row: KendoGridRow = {
                    [GridHelper.GetAlternativeFieldName("Id")]: alternative.Id,
                    [GridHelper.GetAlternativeFieldName("Name")]: alternative.Name,
                    [GridHelper.GetAlternativeFieldName("Description")]: alternative.Description,
                    [GridHelper.GetAlternativeFieldName("Created")]: new Date(alternative.Created),
                    [GridHelper.GetAlternativeFieldName("Modified")]: alternative.Modified ? new Date(alternative.Modified) : null,
                    [GridHelper.GetAlternativeFieldName("StatusName")]: alternative.StatusName,
                };

                this.attributes.forEach(attr => 
                {
                    row[GridHelper.GetAttributeFieldName(attr.Id)] = alternative.Attributes.find(av => av.AttributeId === attr.Id)?.Value
                });

                this.criteria.forEach(c =>
                {
                    let value = alternative.Criteria.find(cv => cv.CriterionId === c.Id).Value;
                    value = roundDecimal(value, this.precision);
                    row[GridHelper.GetCriterionFieldName(c.Id)] = value;
                });

                data.push(row);
            });

            //sorting could conflict with grid options
            this.Grid.SetData(data);
        }
    }

    showGridColumnsEditor()
    {
        this.kendoGridColumnsEditorDialogOpened = true;
    }

    showSubcriteriaColumns()
    {
        const sub = this.criteria.filter(a => a.ParentId === this.selectedCriterion.Id);

        if (sub.length > 0)
        {
            const fieldsIds = this.Grid.GetShownFieldsIds();

            for (const c of sub)
            {
                const f = GridHelper.GetCriterionFieldName(c.Id);

                if (fieldsIds.includes(f) === false)
                {
                    fieldsIds.push(f);
                }
            }

            this.Grid.SetColumns(fieldsIds);
        }
    }

    resetGridColumns()
    {
        const nameField = GridHelper.GetAlternativeFieldName("Name");
        const rootField = GridHelper.GetCriterionFieldName(this.selectedCriterion.Id);
        this.Grid.SetColumns([nameField, rootField]);
        this.Grid.Sort(rootField, "desc");
    }

    ExportGridCommand()
    {
        this.Grid.Export();
    }
}


class ScoringVotesSourceSelectorDialogViewModel
{
    page: ResultsPage = null;

    opened: boolean = false;
    defaultSurveyId: number | null = null;
    defaultProjectUserId: number | null = null;

    show(page: ResultsPage)
    {
        this.page = page;
        this.defaultSurveyId = page.selectedSurvey?.Id ?? null;
        this.defaultProjectUserId = page.selectedProjectUser?.Id ?? null;

        this.opened = true;
    }
}



class ViewSettings
{
    ViewDefId: string;
    ScoringSurveyId: number;
    Precision: number;
}

class GridHelper
{
    static GetAttributeFieldName(attrId: number): string
    {
        return `CommonFieldsAttribute${attrId}`;
    }

    static GetAlternativeFieldName(name: string): string
    {
        return `CommonFields${name}`;
    }

    static GetCriterionFieldName(criterionId: number): string
    {
        return `CriteriaFieldsCriterion${criterionId}`;
    }
}